import React, { Component } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import Preloader from '../../../components/preloader';
import Auth from '../../../auth/auth';

// MS SQL Server has two namespace levels (database → schema); every other engine
// has one. Only for MSSQL does this component render a second dropdown and emit
// an object — see the onChange contract note below.
const isTwoLevel = (connectionType) => String(connectionType || '').toLowerCase() === 'mssql';

/**
 * Schema picker with two shapes.
 *
 * Single-level (default, and what every pre-existing caller gets):
 *   one dropdown, `onChange(schemaNameString)`.
 *   db-add-edit-query.js relies on this exact contract and passes no
 *   connectionType, so it is unaffected by the two-level path below.
 *
 * Two-level (connectionType === 'mssql'):
 *   a database dropdown plus a single schema dropdown, emitting
 *   `onChange({databaseName, schemaName})`.
 *
 * One schema per dictionary. Selecting several used to be the only way to keep a
 * foreign key that crossed schemas; the import now resolves those against the
 * schemas already in the application, in both directions, so the order schemas
 * are imported in no longer matters. Schemas that already belong to a dictionary
 * are shown disabled — one dictionary per schema is enforced by the import, and
 * finding that out here beats finding it out on the wizard's last step.
 */
class SchemaPicker extends Component {
  state = {
    loading: false,
    schemas: [],
    error: '',
    // Two-level only.
    innerSchemas: [],
    innerLoading: false,
    // schemaName → the dictionary that already holds it.
    takenSchemas: {},
  };

  componentDidMount() {
    this.loadSchemas(this.props.connectionId);
    // The wizard restores its state from localStorage, so a chosen database can
    // arrive without handleDatabaseChange ever firing in this mount. Repopulate the
    // schema list instead of leaving it empty under a database that looks selected.
    this.syncInnerSchemas();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.connectionId !== this.props.connectionId) {
      this.loadSchemas(this.props.connectionId);
    }
    if (this.currentDatabaseName(prevProps) !== this.currentDatabaseName(this.props)) {
      this.syncInnerSchemas();
    }
  }

  currentDatabaseName = (props) => (isTwoLevel(props.connectionType) && props.value && props.value.databaseName) || '';

  syncInnerSchemas = () => {
    const databaseName = this.currentDatabaseName(this.props);
    if (databaseName) {
      this.loadInnerSchemas(databaseName);
    } else if (this.state.innerSchemas.length > 0) {
      this.setState({ innerSchemas: [] });
    }
  }

  authHeaders = () => {
    const auth = new Auth();
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-access-token': auth.getAccessToken(),
    };
  }

  extractSchemaNames = (payload) => {
    const response = payload?.response;
    const list = Array.isArray(response)
      ? response
      : Array.isArray(response?.schemas)
        ? response.schemas
        : [];

    return list
      .map((item) => (typeof item === 'string' ? item : item?.name))
      .filter(Boolean);
  }

  loadSchemas = async (connectionId) => {
    if (!connectionId) {
      this.setState({ loading: false, schemas: [], error: '' });
      return;
    }

    this.setState({ loading: true, schemas: [], error: '' });

    try {
      const auth = new Auth();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/manageDb/connections/${connectionId}/schemas`,
        {
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'x-access-token': auth.getAccessToken(),
          },
        }
      );
      const json = await response.json();

      if (!response.ok || json.status === false) {
        throw new Error(json.error || 'Schema load failed');
      }

      this.setState({
        loading: false,
        schemas: this.extractSchemaNames(json),
        error: '',
      });
    } catch (error) {
      this.setState({
        loading: false,
        schemas: [],
        error: "Couldn't load schemas — check the connection",
      });
    }
  }

  handleChange = (event) => {
    if (this.props.onChange) {
      this.props.onChange(event.target.value);
    }
  }

  // ── Two-level (MSSQL) ─────────────────────────────────────────────────────

  loadInnerSchemas = async (databaseName) => {
    const { connectionId } = this.props;
    if (!connectionId || !databaseName) {
      this.setState({ innerSchemas: [], innerLoading: false, takenSchemas: {} });
      return;
    }
    this.setState({ innerLoading: true, innerSchemas: [], error: '' });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/manageDb/connections/${connectionId}` +
        `/databases/${encodeURIComponent(databaseName)}/schemas`,
        { cache: 'no-store', headers: this.authHeaders() }
      );
      const json = await response.json();
      if (!response.ok || json.status === false) {
        throw new Error(json.error || 'Schema load failed');
      }
      this.setState({ innerLoading: false, innerSchemas: this.extractSchemaNames(json) });
    } catch (error) {
      this.setState({
        innerLoading: false,
        innerSchemas: [],
        error: "Couldn't load schemas for that database",
      });
    }
    this.loadTakenSchemas(databaseName);
  }

  // Which of this database's schemas already belong to a dictionary. Failure is
  // deliberately silent: the annotation is a courtesy, and the import enforces the
  // rule regardless — a dead endpoint must not block the picker.
  loadTakenSchemas = async (databaseName) => {
    const { connectionId, excludeDictionaryName } = this.props;
    if (!connectionId || !databaseName) {
      this.setState({ takenSchemas: {} });
      return;
    }
    // Switching databases quickly can land responses out of order; only the newest
    // request may write, or a stale answer greys out the wrong schemas.
    const requestId = (this._takenRequestId || 0) + 1;
    this._takenRequestId = requestId;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dataDictionary/imported-schemas` +
        `?dbConnectionId=${encodeURIComponent(connectionId)}` +
        `&databaseName=${encodeURIComponent(databaseName)}`,
        { cache: 'no-store', headers: this.authHeaders() }
      );
      const json = await response.json();
      if (this._takenRequestId !== requestId) return;
      if (!response.ok || json.status === false) return;
      const taken = {};
      (json.response || []).forEach((row) => {
        // The dictionary being edited does not hold its own schema against itself.
        if (!row || !row.schemaName) return;
        if (excludeDictionaryName && row.instanceName === excludeDictionaryName) return;
        taken[row.schemaName] = row.instanceName;
      });
      this.setState({ takenSchemas: taken });
    } catch (error) {
      if (this._takenRequestId !== requestId) return;
      this.setState({ takenSchemas: {} });
    }
  }

  handleDatabaseChange = (event) => {
    const databaseName = event.target.value;
    // Selecting a different database invalidates any schema selection under it.
    if (this.props.onChange) {
      this.props.onChange({ databaseName, schemaName: '' });
    }
    this.loadInnerSchemas(databaseName);
  }

  handleSchemaOnlyChange = (event) => {
    if (this.props.onChange) {
      this.props.onChange({
        databaseName: (this.props.value || {}).databaseName || '',
        schemaName: event.target.value,
      });
    }
  }

  renderTwoLevel() {
    const { value, disabled } = this.props;
    const { loading, schemas, error, innerSchemas, innerLoading, takenSchemas } = this.state;
    const databaseName = (value || {}).databaseName || '';
    const schemaName = (value || {}).schemaName || '';

    if (loading) {
      return <Preloader loading={true} />;
    }

    return (
      <div>
        <FormControl fullWidth disabled={disabled} error={!!error}>
          <InputLabel>Select database</InputLabel>
          <Select
            value={databaseName}
            onChange={this.handleDatabaseChange}
            SelectDisplayProps={{'data-testid': 'dd-database-select'}}
          >
            {schemas.length === 0 && <MenuItem value="">No databases found on this server</MenuItem>}
            {schemas.map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
          {error ? <FormHelperText>{error}</FormHelperText> : null}
        </FormControl>

        <FormControl fullWidth disabled={disabled || !databaseName || innerLoading}>
          <InputLabel>Select schema</InputLabel>
          <Select
            value={schemaName}
            onChange={this.handleSchemaOnlyChange}
            SelectDisplayProps={{'data-testid': 'dd-schema-select'}}
          >
            {innerSchemas.map((name) => (
              <MenuItem
                key={name}
                value={name}
                disabled={!!takenSchemas[name]}
                data-testid={takenSchemas[name] ? 'dd-schema-option-taken' : 'dd-schema-option'}
              >
                {takenSchemas[name] ? `${name} — already in "${takenSchemas[name]}"` : name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {databaseName
              ? 'One schema per dictionary. Foreign keys to other schemas are linked automatically once those schemas are imported.'
              : 'Pick a database first.'}
          </FormHelperText>
        </FormControl>
      </div>
    );
  }

  render() {
    if (isTwoLevel(this.props.connectionType)) {
      return this.renderTwoLevel();
    }

    const { value, disabled, label } = this.props;
    const { loading, schemas, error } = this.state;
    const pickerLabel = label || 'Select schema';

    if (loading) {
      return <Preloader loading={true} />;
    }

    if (error) {
      return (
        <FormControl fullWidth error={true}>
          <InputLabel>{pickerLabel}</InputLabel>
          <Select value="" disabled={true}>
            <MenuItem value="">No schemas</MenuItem>
          </Select>
          <FormHelperText>{error}</FormHelperText>
        </FormControl>
      );
    }

    if (schemas.length === 0) {
      return (
        <FormControl fullWidth>
          <InputLabel>{pickerLabel}</InputLabel>
          <Select value="" disabled={true}>
            <MenuItem value="">No schemas found on this server</MenuItem>
          </Select>
        </FormControl>
      );
    }

    return (
      <FormControl fullWidth disabled={disabled}>
        <InputLabel>{pickerLabel}</InputLabel>
        <Select value={value || ''} onChange={this.handleChange} SelectDisplayProps={{'data-testid': 'dd-schema-select'}}>
          {schemas.map((schema) => (
            <MenuItem key={schema} value={schema}>{schema}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
}

export default SchemaPicker;
